/* PMP — Buy Now upsell (2026-08-20)
 *
 * Why this exists: ADD TO CART sends the shopper to /cart, where the
 * "You may also like" row gets a chance to raise the basket. BUY NOW skipped
 * that step entirely and went straight to the Stripe overlay, so every
 * Buy Now order lost the upsell impression.
 *
 * What it does: intercepts the FIRST click on the product-page Buy Now button,
 * shows a small complementary-product dialog, then replays the very same click
 * so the existing Stripe flow runs untouched. Nothing else on the product page,
 * the cart page or the Stripe checkout is modified.
 *
 * Why a window-level capture listener: the Stripe Buy Now handler is bound with
 * document.addEventListener('click', fn, true). The capture path always reaches
 * window before document, so this runs first regardless of load order — and it
 * runs AFTER the tap guard (also on window, registered earlier in the head),
 * so accidental scroll-taps are still blocked before we open anything.
 */
(function () {
  'use strict';

  if (window.__pmpBuyNowUpsell) return;
  window.__pmpBuyNowUpsell = true;
  if (location.pathname.indexOf('/products/') === -1) return;

  var LANG = String(
    (window.Shopify && Shopify.locale) || document.documentElement.lang || "en"
  ).replace("_", "-").toLowerCase().split("-")[0];
  var ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
  if (ROOT.slice(-1) !== "/") ROOT += "/";
  function route(path) { return ROOT + String(path || "").replace(/^\/+/, ""); }

  var COPY = {
    en: {
      eyebrow: "Recommended with your order", title: "Complete your dog’s routine",
      intro: "One click to add it — your secure checkout opens right after.",
      add: "Add", adding: "Adding…", added: "Added ✓",
      decline: "No thanks, continue to checkout", close: "Close and continue to checkout",
      error: "Could not add this product."
    },
    fr: {
      eyebrow: "Recommandé avec votre commande", title: "Complétez la routine de votre chien",
      intro: "Ajouté en un clic — votre paiement sécurisé s’ouvre juste après.",
      add: "Ajouter", adding: "Ajout…", added: "Ajouté ✓",
      decline: "Non merci, passer au paiement", close: "Fermer et passer au paiement",
      error: "Impossible d’ajouter ce produit."
    },
    de: {
      eyebrow: "Empfohlen zu Ihrer Bestellung", title: "Vervollständigen Sie die Routine Ihres Hundes",
      intro: "Mit einem Klick hinzufügen — anschließend öffnet sich die sichere Zahlung.",
      add: "Hinzufügen", adding: "Wird hinzugefügt…", added: "Hinzugefügt ✓",
      decline: "Nein danke, weiter zur Zahlung", close: "Schließen und weiter zur Zahlung",
      error: "Dieses Produkt konnte nicht hinzugefügt werden."
    },
    es: {
      eyebrow: "Recomendado con tu pedido", title: "Completa la rutina de tu perro",
      intro: "Añádelo con un clic; el pago seguro se abrirá justo después.",
      add: "Añadir", adding: "Añadiendo…", added: "Añadido ✓",
      decline: "No, gracias; continuar al pago", close: "Cerrar y continuar al pago",
      error: "No se pudo añadir este producto."
    },
    it: {
      eyebrow: "Consigliato con il tuo ordine", title: "Completa la routine del tuo cane",
      intro: "Aggiungilo con un clic: subito dopo si aprirà il pagamento sicuro.",
      add: "Aggiungi", adding: "Aggiunta in corso…", added: "Aggiunto ✓",
      decline: "No grazie, continua al pagamento", close: "Chiudi e continua al pagamento",
      error: "Impossibile aggiungere questo prodotto."
    },
    pt: {
      eyebrow: "Recomendado com a sua encomenda", title: "Complete a rotina do seu cão",
      intro: "Adicione com um clique — o pagamento seguro abre logo a seguir.",
      add: "Adicionar", adding: "A adicionar…", added: "Adicionado ✓",
      decline: "Não, obrigado; continuar para o pagamento", close: "Fechar e continuar para o pagamento",
      error: "Não foi possível adicionar este produto."
    }
  };
  var T = COPY[LANG] || COPY.en;
  var INTL_LOCALE = {
    fr: "fr-FR", de: "de-DE", es: "es-ES", it: "it-IT", pt: "pt-PT", en: "en-US"
  }[LANG] || "en-US";

  var MAX_OFFERS = 2;
  var recommendations = null; /* Promise, primed on first intent */

  /* ---------- helpers ---------- */

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function money(cents, currency) {
    var value = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(INTL_LOCALE, {
        style: 'currency',
        currency: currency || 'USD'
      }).format(value);
    } catch (e) {
      return '$' + value.toFixed(2);
    }
  }

  function currentProductId() {
    try {
      var meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
      if (meta && meta.product && meta.product.id) return meta.product.id;
    } catch (e) {}
    var el = document.querySelector('[data-product-id]');
    return el ? el.getAttribute('data-product-id') : null;
  }

  function cartProductIds() {
    return fetch(route('cart.js'), { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : { items: [] }; })
      .then(function (cart) {
        var map = {};
        (cart.items || []).forEach(function (i) { map[i.product_id] = true; });
        return { inCart: map, currency: cart.currency || 'USD' };
      })
      .catch(function () { return { inCart: {}, currency: 'USD' }; });
  }

  /* Same source as the "You may also like" row on /cart. */
  function loadOffers() {
    if (recommendations) return recommendations;
    var pid = currentProductId();
    if (!pid) {
      recommendations = Promise.resolve([]);
      return recommendations;
    }
    recommendations = Promise.all([
      fetch(route('recommendations/products.json?product_id=' + encodeURIComponent(pid) + '&limit=8'), {
        headers: { Accept: 'application/json' }
      }).then(function (r) { return r.ok ? r.json() : { products: [] }; }),
      cartProductIds()
    ]).then(function (results) {
      var products = results[0].products || [];
      var state = results[1];
      return products.filter(function (p) {
        return p.available && String(p.id) !== String(pid) && !state.inCart[p.id];
      }).slice(0, MAX_OFFERS).map(function (p) {
        var variant = (p.variants || []).filter(function (v) { return v.available; })[0] || (p.variants || [])[0];
        if (!variant) return null;
        var image = p.featured_image ? String(p.featured_image) : '';
        if (image.indexOf('//') === 0) image = 'https:' + image;
        return {
          id: variant.id,
          title: p.title,
          url: p.url,
          price: money(variant.price, state.currency),
          image: image
        };
      }).filter(Boolean);
    }).catch(function () { return []; });
    return recommendations;
  }

  /* Warm the request as soon as the shopper shows purchase intent. */
  function primeOnIntent(event) {
    var t = event.target && event.target.closest ? event.target.closest('.pmp-buy-now-stable') : null;
    if (t) loadOffers();
  }
  addEventListener('pointerover', primeOnIntent, { capture: true, passive: true });
  addEventListener('pointerdown', primeOnIntent, { capture: true, passive: true });

  /* ---------- dialog ---------- */

  var STYLE_ID = 'pmp-bnu-style';
  var CSS = [
    '#pmp-bnu{position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;justify-content:center;',
    'padding:16px;background:rgba(20,16,28,.62);backdrop-filter:blur(4px)}',
    '#pmp-bnu *{box-sizing:border-box}',
    '.pmp-bnu__panel{position:relative;width:min(620px,100%);max-height:calc(100dvh - 32px);overflow-y:auto;',
    'padding:clamp(20px,4vw,34px);border-radius:22px;background:#fff;color:#211a2c;',
    'box-shadow:0 28px 80px rgba(33,26,44,.28);font-family:inherit}',
    '.pmp-bnu__close{position:absolute;top:14px;right:14px;display:grid;place-items:center;width:38px;height:38px;',
    'padding:0;border:1px solid #e7e2ec;border-radius:999px;background:#fff;color:#211a2c;font-size:20px;line-height:1;cursor:pointer}',
    '.pmp-bnu__eyebrow{margin:0;color:#7656d8;font-size:.72rem;font-weight:750;letter-spacing:.11em;text-transform:uppercase}',
    '.pmp-bnu__title{max-width:480px;margin:7px 44px 6px 0;font-size:clamp(1.35rem,4vw,1.9rem);line-height:1.14}',
    '.pmp-bnu__intro{margin:0 0 20px;color:#625b6d;font-size:.95rem}',
    '.pmp-bnu__offers{display:grid;gap:12px}',
    '.pmp-bnu__offer{display:grid;grid-template-columns:96px minmax(0,1fr);gap:16px;padding:14px;',
    'border:1px solid #e8e3ee;border-radius:16px;background:#fbf9fd;align-items:center}',
    '.pmp-bnu__media{display:grid;width:96px;min-height:96px;place-items:center;overflow:hidden;border-radius:12px;background:#fff}',
    '.pmp-bnu__media img{width:100%;height:100%;object-fit:contain}',
    '.pmp-bnu__name{margin:0 0 10px;font-size:1rem;line-height:1.3}',
    '.pmp-bnu__name a{color:inherit;text-decoration:underline;text-decoration-color:#c8b9e7;text-underline-offset:2px}',
    '.pmp-bnu__row{display:flex;align-items:center;justify-content:space-between;gap:12px}',
    '.pmp-bnu__price{font-size:1rem;font-weight:750;white-space:nowrap}',
    '.pmp-bnu__add{min-height:42px;padding:10px 20px;border:0;border-radius:999px;background:#ff3ea5;color:#fff;',
    'font:inherit;font-size:.88rem;font-weight:750;cursor:pointer}',
    '.pmp-bnu__add[disabled]{opacity:.6;cursor:wait}',
    '.pmp-bnu__error{margin:12px 0 0;color:#b42318;font-size:.86rem;text-align:center}',
    '.pmp-bnu__decline{display:block;margin:18px auto 0;padding:8px 12px;border:0;background:transparent;color:#4f4859;',
    'font:inherit;font-size:.9rem;text-decoration:underline;text-underline-offset:3px;cursor:pointer}',
    '@media(max-width:620px){.pmp-bnu__panel{padding:16px 13px 13px;border-radius:16px}',
    '.pmp-bnu__offer{grid-template-columns:72px minmax(0,1fr);gap:11px;padding:10px}',
    '.pmp-bnu__media{width:72px;min-height:72px}.pmp-bnu__name{font-size:.9rem;margin-bottom:8px}',
    '.pmp-bnu__add{padding:9px 15px;font-size:.8rem}}'
  ].join('');

  function ensureStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var s = document.createElement('style');
    s.id = STYLE_ID;
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  var overlay = null;
  var pendingButton = null;

  function proceed() {
    var button = pendingButton;
    pendingButton = null;
    close();
    if (!button || !button.isConnected) return;
    /* Replay the click; the flag lets it fall through to the Stripe handler. */
    button.dataset.pmpUpsellDone = '1';
    button.click();
  }

  function close() {
    if (overlay) {
      overlay.remove();
      overlay = null;
    }
    document.documentElement.style.overflow = '';
    removeEventListener('keydown', onKeydown, true);
  }

  /* Every exit from the box continues the purchase: the shopper already asked
     to buy when they clicked Buy Now, so X, backdrop and Escape all mean
     "skip the offer, take me to payment" — never "cancel my order". */
  function onKeydown(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      proceed();
    }
  }

  function addOffer(offer, button) {
    var label = button.textContent;
    button.disabled = true;
    button.textContent = T.adding;
    fetch(route('cart/add.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: offer.id, quantity: 1 })
    }).then(function (r) {
      if (!r.ok) throw new Error(T.error);
      button.textContent = T.added;
      try {
        if (window.clarity) clarity('event', 'buynow_upsell_accepted');
        if (window.gtag) gtag('event', 'select_promotion', {
          promotion_name: 'Buy Now upsell',
          items: [{ item_id: String(offer.id), item_name: offer.title }]
        });
      } catch (e) {}
      recommendations = null; /* the cart changed */
      setTimeout(proceed, 320);
    }).catch(function () {
      button.disabled = false;
      button.textContent = label;
      var err = overlay && overlay.querySelector('.pmp-bnu__error');
      if (err) err.hidden = false;
    });
  }

  function open(offers) {
    ensureStyle();
    overlay = document.createElement('div');
    overlay.id = 'pmp-bnu';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', T.title);

    var cards = offers.map(function (o) {
      return '<article class="pmp-bnu__offer">' +
          '<span class="pmp-bnu__media">' + (o.image ? '<img src="' + esc(o.image) + '" alt="' + esc(o.title) + '" loading="eager">' : '') + '</span>' +
          '<div>' +
            '<h3 class="pmp-bnu__name"><a href="' + esc(o.url) + '" target="_blank" rel="noopener">' + esc(o.title) + '</a></h3>' +
            '<div class="pmp-bnu__row">' +
              '<span class="pmp-bnu__price">' + esc(o.price) + '</span>' +
              '<button type="button" class="pmp-bnu__add" data-vid="' + esc(o.id) + '">' + esc(T.add) + '</button>' +
            '</div>' +
          '</div>' +
        '</article>';
    }).join('');

    overlay.innerHTML =
      '<div class="pmp-bnu__panel">' +
        '<button type="button" class="pmp-bnu__close" aria-label="' + esc(T.close) + '">&times;</button>' +
        '<p class="pmp-bnu__eyebrow">' + esc(T.eyebrow) + '</p>' +
        '<h2 class="pmp-bnu__title">' + esc(T.title) + '</h2>' +
        '<p class="pmp-bnu__intro">' + esc(T.intro) + '</p>' +
        '<div class="pmp-bnu__offers">' + cards + '</div>' +
        '<p class="pmp-bnu__error" hidden>' + esc(T.error) + '</p>' +
        '<button type="button" class="pmp-bnu__decline">' + esc(T.decline) + '</button>' +
      '</div>';

    document.body.appendChild(overlay);
    document.documentElement.style.overflow = 'hidden';

    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) { proceed(); return; }
      if (e.target.closest('.pmp-bnu__close') || e.target.closest('.pmp-bnu__decline')) { proceed(); return; }
      var add = e.target.closest('.pmp-bnu__add');
      if (add) {
        var offer = offers.filter(function (o) { return String(o.id) === add.getAttribute('data-vid'); })[0];
        if (offer) addOffer(offer, add);
      }
    });
    addEventListener('keydown', onKeydown, true);

    var first = overlay.querySelector('.pmp-bnu__add') || overlay.querySelector('.pmp-bnu__decline');
    if (first) first.focus();

    try {
      if (window.clarity) clarity('event', 'buynow_upsell_shown');
      if (window.gtag) gtag('event', 'view_promotion', { promotion_name: 'Buy Now upsell' });
    } catch (e) {}
  }

  /* ---------- interception ---------- */

  addEventListener('click', function (e) {
    var button = e.target && e.target.closest ? e.target.closest('.pmp-buy-now-stable') : null;
    if (!button || button.disabled) return;

    /* Replayed click: consume the flag and let the Stripe handler run. */
    if (button.dataset.pmpUpsellDone === '1') {
      delete button.dataset.pmpUpsellDone;
      return;
    }
    if (overlay) { e.preventDefault(); e.stopImmediatePropagation(); return; }

    e.preventDefault();
    e.stopImmediatePropagation();

    pendingButton = button;
    var label = button.textContent;
    button.disabled = true;

    var settled = false;
    /* Never let a slow recommendation request stand between a shopper and
       checkout: after 1.2 s the original click is replayed regardless. */
    var timer = setTimeout(function () {
      if (settled) return;
      settled = true;
      button.disabled = false;
      button.textContent = label;
      proceed();
    }, 1200);

    loadOffers().then(function (offers) {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      button.disabled = false;
      button.textContent = label;
      if (!offers.length) { proceed(); return; }
      open(offers);
    });
  }, true);
})();
